// =============================================================================
// SHANIZ GAMING ADMIN - MAIN LAYOUT (role-aware, grouped nav, global search, bell)
// =============================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  AppBar,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  SportsEsports as GamesIcon,
  EmojiEvents as TournamentIcon,
  AccountBalance as TransactionsIcon,
  Leaderboard as LeaderboardIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  Report as ReportIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  SwapHoriz as ReconIcon,
  SupportAgent as SupportIcon,
  Warning as FraudIcon,
  VerifiedUser as KycIcon,
  Insights as InsightsIcon,
  Flag as FeatureFlagIcon,
  AdminPanelSettings as RolesIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { usePermission, roleAllowed, useAdminRole, PermissionKey } from '../hooks/usePermission';
import { AdminRole } from '../services/api';
import { useAdminSocket, adminSocket } from '../hooks/useAdminSocket';
import GlobalSearch from './GlobalSearch';
import NotificationsBell from './NotificationsBell';
import { ThemeToggle } from './ThemeToggle';

const drawerWidth = 260;

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  permission?: PermissionKey;
  // Role-based visibility (FR-ADM-01). SUPER_ADMIN always sees the item.
  // undefined = visible to every role; [] = SUPER_ADMIN only.
  roles?: AdminRole[];
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    heading: 'Overview',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
      { text: 'Users', icon: <PeopleIcon />, path: '/users', permission: 'users.view' },
      { text: 'Games', icon: <GamesIcon />, path: '/games', permission: 'games.view', roles: ['GAME_ADMIN'] },
      { text: 'Tournaments', icon: <TournamentIcon />, path: '/tournaments', roles: ['GAME_ADMIN'] },
      { text: 'Leaderboard', icon: <LeaderboardIcon />, path: '/leaderboard', roles: ['GAME_ADMIN'] },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { text: 'Payouts', icon: <PaymentIcon />, path: '/payouts', permission: 'payouts.view', roles: ['FINANCE_ADMIN'] },
      { text: 'Reconciliation', icon: <ReconIcon />, path: '/reconciliation', permission: 'reconciliation.run', roles: ['FINANCE_ADMIN'] },
      { text: 'Support', icon: <SupportIcon />, path: '/support', permission: 'tickets.view', roles: ['SUPPORT_AGENT'] },
      { text: 'Fraud & Risk', icon: <FraudIcon />, path: '/fraud', permission: 'fraud.view', roles: ['RISK_ANALYST'] },
      { text: 'Transactions', icon: <TransactionsIcon />, path: '/transactions', roles: ['FINANCE_ADMIN'] },
    ],
  },
  {
    heading: 'Compliance',
    items: [
      { text: 'KYC Review', icon: <KycIcon />, path: '/kyc', permission: 'kyc.view', roles: ['RISK_ANALYST', 'SUPPORT_AGENT'] },
      { text: 'Audit Logs', icon: <HistoryIcon />, path: '/audit-logs', permission: 'audit.view', roles: ['FINANCE_ADMIN', 'RISK_ANALYST'] },
      { text: 'Player Reports', icon: <ReportIcon />, path: '/reports', roles: ['RISK_ANALYST', 'SUPPORT_AGENT'] },
      { text: 'Anti-Cheat', icon: <SecurityIcon />, path: '/anticheat', roles: ['GAME_ADMIN', 'RISK_ANALYST'] },
      { text: 'Analytics', icon: <InsightsIcon />, path: '/analytics', permission: 'reports.view', roles: ['FINANCE_ADMIN', 'GAME_ADMIN'] },
    ],
  },
  {
    heading: 'Settings',
    items: [
      { text: 'Roles', icon: <RolesIcon />, path: '/roles', permission: 'roles.view', roles: [] },
      { text: 'Feature Flags', icon: <FeatureFlagIcon />, path: '/feature-flags', permission: 'feature_flags.view', roles: ['GAME_ADMIN'] },
      { text: 'System', icon: <SettingsIcon />, path: '/settings', roles: [] },
    ],
  },
];

const NavLinkItem: React.FC<{ item: NavItem; active: boolean; onClick: () => void }> = ({ item, active, onClick }) => {
  const role = useAdminRole();
  const perm = usePermission((item.permission || 'users.view') as PermissionKey);
  const permDenied = Boolean(item.permission) && !perm.allowed;
  const roleDenied = !roleAllowed(role, item.roles);
  if (permDenied || roleDenied) return null;
  return (
    <ListItem disablePadding sx={{ px: 1, mb: 0.5 }}>
      <ListItemButton
        onClick={onClick}
        selected={active}
        sx={{
          borderRadius: 2,
          color: 'text.secondary',
          '& .MuiListItemIcon-root': { color: 'text.secondary' },
          '&:hover': {
            backgroundColor: 'action.hover',
            color: 'text.primary',
            '& .MuiListItemIcon-root': { color: 'text.primary' },
          },
          '&.Mui-selected': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
            color: 'primary.main',
            fontWeight: 600,
            borderLeft: 3,
            borderColor: 'primary.main',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            pl: 1.25,
            '& .MuiListItemIcon-root': { color: 'primary.main' },
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.16),
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14 }} />
      </ListItemButton>
    </ListItem>
  );
};

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Mount the socket connection from the Layout level so the ws-status dot
  // in the sidebar reflects current state. Individual listeners (like the
  // NotificationsBell) subscribe via their own useAdminSocket() call.
  const { status: wsStatus } = useAdminSocket();

  useEffect(() => () => adminSocket.disconnect(), []);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Shanzz"
            sx={{ width: 32, height: 32, borderRadius: 1 }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Shanzz Admin</Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: 'text.primary' }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', pt: 1 }}>
        {navGroups.map((group) => (
          <Box key={group.heading} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 3, display: 'block', fontWeight: 700, letterSpacing: 1 }}>
              {group.heading.toUpperCase()}
            </Typography>
            <List sx={{ py: 0 }}>
              {group.items.map((item) => (
                <NavLinkItem
                  key={item.path}
                  item={item}
                  active={location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">v1.0.0 · Shanzz Gaming</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: wsStatus === 'open' ? 'success.main' : wsStatus === 'connecting' ? 'warning.main' : 'error.main' }} />
          <Typography variant="caption" color="text.secondary">
            live feed {wsStatus}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>

          <Box sx={{ flex: 1 }} />

          <GlobalSearch />
          <NotificationsBell />
          <ThemeToggle />

          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ ml: 1 }} aria-label="Account menu">
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 }}>
              {user?.name?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Stack>
                <Typography variant="body2">{user?.email}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.role}</Typography>
              </Stack>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); logout(); navigate('/login'); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'background.default',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${drawerWidth}px)` }, mt: 8, bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
